import React from "react";
import styled from "styled-components";
import { Color, FontWeight, Spacing, form, transparentize } from "@glossy";
import Icon, { Glyph } from "@ui-kit/icon";
import Loading from "@ui-kit/loading";

interface IProps {
  label: React.ReactNode;
  size?: Size;
  maxWidth?: number;
  status?: Status;
  loading?: boolean;
  glyph?: Glyph;
  noLinethrough?: boolean;
  onClick?: (event?: any) => void;
  onClickClose?: () => void;
  className?: string;
}

// Size
export const SIZES = {
  small: { height: 32 },
  medium: { height: 36 },
  large: { height: 48 },
};
export type Size = keyof typeof SIZES;

// Status
export const STATUS = ["none", "selected", "include", "exclude"] as const;
export type Status = typeof STATUS[number];

const SIDE_PADDING = 16;

const statusColor = (props) => {
  const colors = {
    include: "include",
    exclude: "exclude",
  };
  return form(props, colors[props.status] || "primary");
};

const Root = styled.div<{
  closable: boolean;
  clickable: boolean;
  size: Size;
  status: Status;
}>`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: ${(props) => statusColor(props)};
  font-weight: ${(props) => props.status !== "none" && FontWeight.Normal};
  box-sizing: border-box;
  line-height: ${(props) => SIZES[props.size].height - 2}px;
  min-height: ${(props) => SIZES[props.size].height}px;
  padding: 0 ${SIDE_PADDING - 1}px;
  padding-right: ${(props) => !!props.closable && 0};
  border-style: solid;
  border-width: 1px;
  border-color: ${(props) =>
    props.status === "none"
      ? transparentize(0.5, statusColor(props))
      : "transparent"};
  border-radius: ${(props) => SIZES[props.size].height}px;
  background-color: ${(props) =>
    props.status === "none"
      ? "transparent"
      : transparentize(0.85, statusColor(props))};
  cursor: ${(props) => props.clickable && "pointer"};

  :not(:hover) {
    border-style: ${(props) => props.status === "none" && "dashed"};
    border-color: ${(props) =>
      props.status === "none"
        ? transparentize(0.5, form(props, "primary"))
        : "transparent"};
    opacity: ${(props) => props.status === "none" && 0.5};
  }

  :active,
  :focus,
  &[aria-expanded="true"] {
    border-style: solid;
    border-color: ${(props) =>
      props.clickable || props.closable ? statusColor(props) : "transparent"};
    box-shadow: ${(props) =>
      props.clickable || props.closable
        ? `inset 0 0 0 1px ${statusColor(props)}`
        : "inset 0 0 0 0"};
    opacity: 1;
  }

  :hover,
  :active,
  :focus,
  &[aria-expanded="true"] {
    background-color: ${(props) =>
      props.status === "none"
        ? "transparent"
        : transparentize(0.8, statusColor(props))};
  }
`;

interface LabelProps {
  closable: boolean;
  status: Status;
  maxWidth: number;
  noLinethrough: boolean;
}
const Label = styled.div.attrs(({ maxWidth, closable }: LabelProps) => ({
  stop1: `${Color.Black} ${maxWidth - 32 - 48 - (closable ? 15 : 0)}px`,
  stop2: `transparent ${maxWidth - 32 - (closable ? 15 : 0)}px`,
}))<LabelProps>`
  display: inline-block;
  text-decoration: ${(props) =>
    props.status === "exclude" && !props.noLinethrough && "line-through"};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  mask-image: ${(props) =>
    `linear-gradient(to right, ${props.stop1}, ${props.stop2})`};
`;

const ClickZone = styled.div<{ isLarge: boolean; status?: Status }>`
  display: inline-block;
  line-height: 0;
  padding: 8px;
  margin: ${(props) => (props.isLarge ? 5 : 3)}px;
  border-radius: 32px;
  vertical-align: middle;
  cursor: pointer;
  :hover {
    background-color: ${(props) => transparentize(0.85, statusColor(props))};
  }
`;

const StyledLoading = styled(Loading)`
  margin: 0 ${Spacing.ExtraSmall};
`;

const StyledIcon = styled(Icon)`
  margin-right: ${Spacing.ExtraSmall};
`;

const UiKitFormChip = React.forwardRef(
  (
    {
      label,
      size = "small",
      maxWidth = 240,
      status,
      loading,
      glyph,
      noLinethrough = false,
      onClick,
      onClickClose,
      className,
      ...props
    }: IProps,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <Root
      size={size}
      style={{ maxWidth }}
      status={status}
      closable={!!onClickClose}
      onClick={onClick}
      clickable={!!onClick}
      className={className}
      ref={ref}
      {...props}
    >
      {loading ? (
        <StyledLoading size="s" cover={false} inline />
      ) : (
        <Label
          status={status}
          closable={!!onClickClose}
          maxWidth={maxWidth}
          noLinethrough={noLinethrough}
        >
          {!!glyph && <StyledIcon glyph={glyph} />}
          <span>{label}</span>
        </Label>
      )}
      {!!onClickClose && (
        <ClickZone
          isLarge={size === "large"}
          status={status}
          onClick={(e) => {
            onClickClose();
            e.stopPropagation();
          }}
        >
          <Icon glyph="cross-thick" size="xs" />
        </ClickZone>
      )}
    </Root>
  )
);

export default UiKitFormChip;
